import React, { useCallback, useRef, useState } from "react";

import { useDispatch } from "react-redux";

import { BusinessAction } from "actions";

import { Autocomplete } from "components/Form/FormikControls";

import { FilterOptionsState } from "@material-ui/lab/useAutocomplete/useAutocomplete";
import { renderOptionWithMatches } from "components/Form/AutoCompleteBusinessPicker/SearchOptions";
import {
  AutoCompleteBusinessPickerProps,
  BusinessAutoCompletePickerProps,
  DeploymentBusinessAutoCompleteItem,
  DjangoPaginatorResponse,
  GlobalBusinessAutoCompleteItem,
  SearchActionProps,
} from "./types";

function BusinessAutoCompletePicker<TOption>(
  props: BusinessAutoCompletePickerProps<TOption>,
) {
  const {
    name,
    disabled,
    renderOption,
    minSearchLength = 3,
    getOptionLabel,
    getOptionValue,
    getSearchAction,
  } = props;

  const [options, setOptions] = useState<TOption[]>([]);
  const termRef = useRef<string>("");

  const [isInFlight, setIsInFlight] = React.useState<boolean>(false);

  const inputDebounceTimeoutRef = useRef<number>(null);

  const dispatch = useDispatch();

  const onSuccess = useCallback(
    (response: DjangoPaginatorResponse<TOption>) => {
      setIsInFlight(false);
      setOptions(response.results);
    },
    [setOptions],
  );

  const onFailure = useCallback(() => {
    setIsInFlight(false);
    setOptions([]);
  }, [setOptions]);

  const requestNewBusinessOptions = useCallback(() => {
    dispatch(
      getSearchAction({
        searchTerm: termRef.current,
        onSuccess,
        onFailure,
      }),
    );

    setIsInFlight(true);
  }, [getSearchAction, termRef, dispatch, onFailure, onSuccess]);

  const filterOptions = useCallback(
    (paramOptions: TOption[], state: FilterOptionsState<TOption[]>) => {
      if (
        state.inputValue.length >= minSearchLength &&
        state.inputValue !== termRef.current
      ) {
        termRef.current = state.inputValue;
        if (inputDebounceTimeoutRef.current) {
          clearTimeout(inputDebounceTimeoutRef.current);
        }
        inputDebounceTimeoutRef.current = setTimeout(
          requestNewBusinessOptions,
          400,
        ) as any;
      }
      return paramOptions;
    },
    [requestNewBusinessOptions, minSearchLength],
  );

  return (
    <Autocomplete
      name={name}
      options={options}
      filterOptions={filterOptions}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      disabled={disabled}
      placeHolder="Select Business"
      noOptionsText={
        !termRef.current ? "Start Typing For Results" : "No Results"
      }
      loading={isInFlight}
    />
  );
}

export function GlobalBusinessAutoCompletePicker(
  props: AutoCompleteBusinessPickerProps,
) {
  const { name, disabled } = props;

  const getSearchAction = useCallback(
    ({
      searchTerm,
      onSuccess,
      onFailure,
    }: SearchActionProps<GlobalBusinessAutoCompleteItem>): object =>
      BusinessAction.getGlobalBusinessesForAutoComplete(searchTerm, {
        onSuccess,
        onFailure,
      }),
    [],
  );

  return (
    <BusinessAutoCompletePicker<GlobalBusinessAutoCompleteItem>
      name={name}
      renderOption={renderOptionWithMatches}
      getOptionLabel={option => option.name}
      getOptionValue={option => option?.id}
      disabled={disabled}
      getSearchAction={getSearchAction}
    />
  );
}

export function DeploymentBusinessAutoCompletePicker(
  props: AutoCompleteBusinessPickerProps,
) {
  const { name, disabled, userRole } = props;

  const getSearchAction = useCallback(
    ({
      searchTerm,
      onSuccess,
      onFailure,
    }: SearchActionProps<DeploymentBusinessAutoCompleteItem>): object =>
      BusinessAction.getBusinessesForAutoComplete(searchTerm, userRole, {
        onSuccess,
        onFailure,
      }),
    [userRole],
  );

  return (
    <BusinessAutoCompletePicker<DeploymentBusinessAutoCompleteItem>
      name={name}
      getOptionLabel={option => option.name}
      getOptionValue={option => option?.id}
      disabled={disabled}
      getSearchAction={getSearchAction}
    />
  );
}
